// src/pages/sow-analysis/SowAnalysisPage.jsx
//
// SOW Analysis page -- browse history from every source (manual uploads
// today; SFDC-triggered once that pipeline is built) and trigger a new
// manual analysis, in one place. Merges what used to be two separate
// pages (SOW History Analysis + SOW Analysis Viewer). State/handlers live
// in useSowAnalysisPage(); this file is composition only -- see
// AnalysisHistoryTable.jsx, UploadSowModal.jsx, and AnalysisDetailModal.jsx
// for the rest.

import { Alert, PageHeader } from "../../shared/ui";
import RegisterProjectModal from "../../accounts/components/RegisterProjectModal";
import { useSowAnalysisPage } from "./useSowAnalysisPage";
import UploadSowModal from "./UploadSowModal";
import AnalysisHistoryTable from "./AnalysisHistoryTable";
import AnalysisDetailModal from "./AnalysisDetailModal";
import { X } from "lucide-react";

export default function SowAnalysisPage() {
  const h = useSowAnalysisPage();

  return (
    <div>
      <PageHeader
        title="SOW Analysis"
        subtitle={
          h.userEmail
            ? `View analysis history from SFDC and manual uploads, or analyze a SOW now. Viewing as: ${h.userEmail}`
            : "View analysis history from SFDC and manual uploads, or analyze a SOW now."
        }
      />

      {h.message && (
        <Alert variant="success" className="mb-4">
          {h.message}
        </Alert>
      )}
      {h.error && !h.uploadModalOpen && (
        <Alert variant="danger" className="mb-4">
          {h.error}
        </Alert>
      )}

      <AnalysisHistoryTable
        activeTab={h.activeTab}
        onTabChange={h.setActiveTab}
        opportunities={h.opportunities}
        engagements={h.engagements}
        selectedFilterValue={h.selectedFilterValue}
        onFilterChange={h.setSelectedFilterValue}
        onOpenUpload={h.openUploadModal}
        rows={h.analysisHistory}
        filteredRows={h.filteredHistory}
        loading={h.loading}
        search={h.search}
        onSearchChange={h.setSearch}
        onSelect={h.selectAnalysis}
      />

      <UploadSowModal
        open={h.uploadModalOpen}
        onClose={h.closeUploadModal}
        onSubmit={h.handleAnalyze}
          onImportPlanningSow={h.handleImportPlanning}
        accountName={h.currentAccount?.name}
        engagements={h.engagements}
        selectedEngagementId={h.selectedEngagementId}
        onEngagementChange={h.setSelectedEngagementId}
        canCreateProject={h.canCreateProject}
        onNewProject={() => h.setNewProjectModalOpen(true)}
        opportunityLinked={h.opportunityLinked}
        form={h.uploadForm}
        onFormChange={h.onUploadFormChange}
        files={h.files}
        onFilesChange={h.setFiles}
        busy={h.busy}
        progress={h.progress}
        statusText={h.statusText}
        error={h.error}
      />

      <RegisterProjectModal
        open={h.newProjectModalOpen}
        accountName={h.currentAccount?.name}
        onClose={() => h.setNewProjectModalOpen(false)}
        onRegister={h.registerProject}
      />

      <AnalysisDetailModal
        open={h.detailModalOpen}
        onClose={h.closeDetailModal}
        detail={h.selectedDetail}
        loading={h.detailLoading}
        onDownloadExcel={h.downloadExcel}
        downloadingExcel={h.downloadingExcel}
        onDownloadSowFile={h.downloadSowFile}
        downloadingSowFile={h.downloadingSowFile}
        onDownloadJson={h.downloadJson}
      />

      {/* Floating Background Progress Bars for active jobs */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-50">
        {!h.uploadModalOpen && h.activeJobs.map(job => (
          <div
            key={job.id}
            className={`w-80 bg-white border shadow-xl rounded-lg p-4 cursor-pointer transition-colors group ${job.error ? 'border-red-300 hover:border-red-500' : 'border-gray-200 hover:border-brand-300'}`}
            onClick={() => h.openUploadModalForJob(job.id)}
          >
            <div className="flex justify-between items-center mb-2">
              <span className={`text-sm font-semibold transition-colors ${job.error ? 'text-red-700 group-hover:text-red-800' : 'text-gray-800 group-hover:text-brand-600'}`}>
                {job.error ? "Analysis Failed" : "Analyzing SOWs..."}
              </span>
              {!job.error && <span className="text-xs font-medium text-gray-500">{job.progress}%</span>}
              {job.error && (
                <button
                  type="button"
                  onClick={(e) => h.dismissJob(e, job.id)}
                  className="text-red-500 hover:text-red-700 p-0.5 rounded-full hover:bg-red-50 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {!job.error && (
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden mb-2">
                <div
                  className="h-full rounded-full bg-brand-600 transition-all duration-300 ease-out"
                  style={{ width: `${Math.round(Math.min(100, Math.max(0, job.progress)))}%` }}
                />
              </div>
            )}
            <div className={`text-xs truncate ${job.error ? 'text-red-500' : 'text-gray-500'}`}>
              {job.error ? "Click to view error details" : (job.statusText || "Processing in background...")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
